declare global {
	interface Window {
		android: any;
		webkit: any;
		opera: any;
	}
	interface Navigator {
		userAgent: any;
		vendor: any;
	}
}

export type DeviceType = 'mobile' | 'tablet' | 'pc';

export const checkPlatform = () => {
	if (typeof window !== 'undefined') {
		const userAgent = navigator.userAgent || navigator.vendor || window.opera;
		const checker = {
			iphone:
				[
					'iPad Simulator',
					'iPhone Simulator',
					'iPod Simulator',
					'iPad',
					'iPhone',
					'iPod',
				].includes(navigator.platform) ||
				(navigator.userAgent.includes('Mac') && 'ontouchend' in document),
			android: userAgent.match(/Android/),
		};
		return checker;
	}

	return {
		iphone: false,
		android: false,
	};
};

export const currentOS = checkPlatform();

export const isMobile = !!currentOS?.android || !!currentOS?.iphone;

/**
 * Check if the device is a tablet
 */
export const checkIsTablet = (): boolean => {
	if (typeof window === 'undefined') return false;

	const userAgent = navigator.userAgent.toLowerCase();
	const platform = navigator.platform?.toLowerCase() || '';

	// iPad detection - multiple methods
	const isIPad =
		/ipad/.test(userAgent) ||
		/ipad/.test(platform) ||
		(platform === 'macintel' && navigator.maxTouchPoints > 1) ||
		// Chrome DevTools iPad emulation
		(/macintosh/.test(userAgent) && navigator.maxTouchPoints > 0);

	// Android tablet detection (Android without "mobile" keyword typically means tablet)
	const isAndroidTablet =
		/android/.test(userAgent) && !/mobile/.test(userAgent);

	// Samsung tablet detection (SM-T pattern)
	const isSamsungTablet = /sm-t|gt-p|gt-n|sch-i/.test(userAgent);

	// Windows tablet detection
	const isWindowsTablet = /windows/.test(userAgent) && /touch/.test(userAgent);

	// Screen size based detection for Chrome DevTools emulation
	// Tablets typically have width >= 600px and not a desktop
	const screenWidth = window.innerWidth;
	const screenHeight = window.innerHeight;
	const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
	const isTabletScreenSize = screenWidth >= 600 && screenWidth <= 1400;
	const isPortraitOrLandscape = screenWidth !== screenHeight;

	// Chrome DevTools emulation fallback - touch + tablet screen size + not phone UA
	const isChromeEmulatedTablet =
		hasTouch &&
		isTabletScreenSize &&
		isPortraitOrLandscape &&
		!/iphone|ipod/.test(userAgent) &&
		(!/mobile/.test(userAgent) || /android/.test(userAgent));

	return (
		isIPad ||
		isAndroidTablet ||
		isSamsungTablet ||
		isWindowsTablet ||
		isChromeEmulatedTablet
	);
};

/**
 * Check if the device is a mobile phone (not tablet)
 */
export const checkIsMobilePhone = (): boolean => {
	if (typeof window === 'undefined') return false;

	// If it's a tablet, it's not a phone
	if (checkIsTablet()) return false;

	const userAgent = navigator.userAgent.toLowerCase();

	// iPhone detection (but not iPad)
	const isIPhone = /iphone|ipod/.test(userAgent) && !/ipad/.test(userAgent);

	// Android phone detection (Android with "mobile" keyword and small screen)
	const isAndroidPhone =
		/android/.test(userAgent) &&
		(/mobile/.test(userAgent) || window.innerWidth < 600);

	return isIPhone || isAndroidPhone;
};

/**
 * Get device type: 'mobile' | 'tablet' | 'pc'
 */
export const getDeviceType = (): DeviceType => {
	if (checkIsTablet()) return 'tablet';
	if (checkIsMobilePhone()) return 'mobile';
	return 'pc';
};

export const isTablet = checkIsTablet();
export const isMobilePhone = checkIsMobilePhone();
export const deviceType = getDeviceType();
